import { useEffect, useRef, useState, useContext, useCallback } from 'react';
import { io } from 'socket.io-client';
import axios from 'src/axios';
import { APICONSTANTS } from 'src/components/Constants';
import { UserContext, SnackContext } from 'src/store/ContextStore';

const HEARTBEAT_INTERVAL_MS = 30_000;

export function usePedigreeLock(familyId) {
  const { user } = useContext(UserContext);
  const { setSnack } = useContext(SnackContext);
  const [lockState, setLockState] = useState(null);

  // Pending access-request state: null | 'pending' | 'granted' | 'denied'
  const [requestStatus, setRequestStatus] = useState(null);

  // Incoming request from another user (only set on the lock holder's side)
  // null | { requesterName: string, requesterId: string }
  const [pendingRequest, setPendingRequest] = useState(null);

  const socketRef = useRef(null);
  const heartbeatRef = useRef(null);
  const sessionId = useRef(String(Date.now() + Math.random()));

  const userId = user?._id ? String(user._id) : null;
  const isMine = lockState
    ? String(lockState.lockedByUserId) === userId
    : false;

  useEffect(() => {
    if (!familyId) return;

    const socket = io('', { transports: ['websocket', 'polling'] });
    socketRef.current = socket;

    socket.on('connect', () => {
      // Join pedigree room after socket connects
      socket.emit('pedigree:join', { familyId });

      // Acquire lock
      axios
        .post(`${APICONSTANTS.pedigreeLock}/${familyId}/acquire`, {
          sessionId: socket.id || sessionId.current
        })
        .then((res) => {
          setLockState(res.data.lock);
        })
        .catch((err) => console.error('Failed to acquire pedigree lock', err));
    });

    // Lock events from other users
    socket.on('pedigree:lock-acquired', ({ lock }) => {
      setLockState(lock);
    });

    socket.on('pedigree:lock-released', () => {
      setLockState(null);
    });

    socket.on('pedigree:lock-force-acquired', ({ lock }) => {
      setLockState(lock);
    });

    // Someone is requesting edit access from us — show persistent banner
    socket.on(
      'pedigree:lock-request-received',
      ({ requesterName, requesterId, targetUserId }) => {
        if (targetUserId && userId && String(targetUserId) !== userId) return;
        setPendingRequest({ requesterName, requesterId });
      }
    );

    // Requester cancelled their access request — clear our banner
    socket.on(
      'pedigree:lock-request-cancelled',
      ({ requesterId, targetUserId }) => {
        if (targetUserId && userId && String(targetUserId) !== userId) return;
        setPendingRequest((prev) =>
          prev && String(prev.requesterId) === String(requesterId) ? null : prev
        );
      }
    );

    // Response from lock holder to our request
    socket.on(
      'pedigree:lock-request-result',
      ({ granted, targetUserId, declineMessage }) => {
        if (targetUserId && userId && String(targetUserId) !== userId) return;
        setRequestStatus(granted ? 'granted' : 'denied');
        if (granted) {
          // Re-acquire lock now that holder released it
          axios
            .post(`${APICONSTANTS.pedigreeLock}/${familyId}/acquire`, {
              sessionId: socket.id || sessionId.current
            })
            .then((res) => {
              setLockState(res.data.lock);
            })
            .catch(console.error);
        } else {
          const msg = declineMessage
            ? `Your request was declined: "${declineMessage}"`
            : 'Your request for edit access was declined.';
          setSnack({ status: true, severity: 'warning', msg });
        }
      }
    );

    // Start heartbeat
    heartbeatRef.current = setInterval(() => {
      axios
        .post(`${APICONSTANTS.pedigreeLock}/${familyId}/heartbeat`)
        .catch(console.error);
    }, HEARTBEAT_INTERVAL_MS);

    return () => {
      clearInterval(heartbeatRef.current);
      socket.emit('pedigree:leave', { familyId });
      axios
        .post(`${APICONSTANTS.pedigreeLock}/${familyId}/release`)
        .catch(console.error);
      socket.disconnect();
      socketRef.current = null;
    };
  }, [familyId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Release lock on tab close via sendBeacon
  useEffect(() => {
    if (!familyId) return;
    const handleUnload = () => {
      navigator.sendBeacon(`/api/pedigree-lock/${familyId}/release`);
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, [familyId]);

  // eslint-disable-next-line no-unused-vars
  const releaseLock = useCallback(() => {
    axios
      .post(`${APICONSTANTS.pedigreeLock}/${familyId}/release`)
      .catch(console.error);
  }, [familyId]);

  // Lock holder responds to an incoming request (grant or decline with message)
  const respondToRequest = useCallback(
    (granted, declineMessage) => {
      const req = pendingRequest;
      if (!req) return;
      setPendingRequest(null);
      const socket = socketRef.current;
      if (socket) {
        socket.emit('pedigree:lock-release-response', {
          familyId,
          granted,
          requesterId: req.requesterId,
          declineMessage: declineMessage || null
        });
      }
      if (granted) {
        // Release our lock so the requester can acquire it
        axios
          .post(`${APICONSTANTS.pedigreeLock}/${familyId}/release`)
          .catch(console.error);
      }
    },
    [familyId, pendingRequest]
  );

  const requestLock = useCallback(() => {
    if (!lockState) return;
    setRequestStatus('pending');
    // Use the HTTP route so the backend resolves the name from the session
    axios
      .post(`${APICONSTANTS.pedigreeLock}/${familyId}/request`, {
        lockHolderUserId: lockState.lockedByUserId
      })
      .catch(() => setRequestStatus(null));
  }, [familyId, lockState]);

  const cancelRequestLock = useCallback(() => {
    if (!lockState) return;
    setRequestStatus(null);
    const socket = socketRef.current;
    if (socket) {
      socket.emit('pedigree:lock-request-cancel', {
        familyId,
        lockHolderUserId: lockState.lockedByUserId
      });
    }
  }, [familyId, lockState]);

  const forceAcquireLock = useCallback(() => {
    axios
      .post(`${APICONSTANTS.pedigreeLock}/${familyId}/force-acquire`, {
        sessionId: socketRef.current?.id || sessionId.current
      })
      .then((res) => {
        setLockState(res.data.lock);
      })
      .catch((err) =>
        console.error('Failed to force-acquire pedigree lock', err)
      );
  }, [familyId]);

  return {
    lockState,
    isMine,
    requestStatus,
    requestLock,
    cancelRequestLock,
    forceAcquireLock,
    pendingRequest,
    respondToRequest
  };
}
